import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { useSupabase } from './useSupabase';

export type SubscriptionTier = 'basic' | 'pro' | 'enterprise' | null;

export interface TierEntitlements {
  canUseGamification: boolean;
  canUseLeaderboards: boolean;
  canUseDifficultyLevels: boolean;
  canUseAdvancedObjections: boolean;
  canUseCustomerTypes: boolean;
  canAccessManagerDashboard: boolean;
  canUseRollupAnalytics: boolean;
  canUseCrmIntegrations: boolean;
  canUseAudioRecordings: boolean;
  canUseVoiceCoaching: boolean;
  audioRetentionDays: number;
}

// Default entitlements (no access)
const DEFAULT_ENTITLEMENTS: TierEntitlements = {
  canUseGamification: false,
  canUseLeaderboards: false,
  canUseDifficultyLevels: false,
  canUseAdvancedObjections: false,
  canUseCustomerTypes: false,
  canAccessManagerDashboard: false,
  canUseRollupAnalytics: false,
  canUseCrmIntegrations: false,
  canUseAudioRecordings: false,
  canUseVoiceCoaching: false,
  audioRetentionDays: 0,
};

export interface AccessStatusResult {
  allowed: boolean;
  reason: 'user_subscription' | 'dealership_subscription' | 'dealership_no_subscription' | 'no_subscription' | 'founder_access' | null;
  scopeUsed: 'user' | 'dealership' | 'founder' | null;
  status?: string;
  currentPeriodEnd?: string | null;
  hasDealership?: boolean;
  dealershipId?: string;
  tier?: SubscriptionTier;
  entitlements?: TierEntitlements;
  isFounder?: boolean;
}

interface AccessStatusState {
  isLoading: boolean;
  accessStatus: AccessStatusResult | null;
  error: string | null;
}

export function useAccessStatus() {
  const { isSignedIn, loading } = useAuth();
  const supabase = useSupabase();
  const isLoaded = !loading;
  
  const [state, setState] = useState<AccessStatusState>({
    isLoading: true,
    accessStatus: null,
    error: null,
  });
  const hasCheckedRef = useRef(false);

  const checkAccess = useCallback(async () => {
    // Wait for auth to be fully loaded
    if (!isLoaded) {
      return null;
    }

    if (!isSignedIn) {
      setState({
        isLoading: false,
        accessStatus: null,
        error: null,
      });
      return null;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const { data, error } = await supabase.functions.invoke('access-status');

      if (error) {
        console.error('[useAccessStatus] Function invoke error:', error);
        throw new Error(error.message);
      }

      const result = data as AccessStatusResult;
      setState({
        isLoading: false,
        accessStatus: result,
        error: null,
      });

      return result;
    } catch (err) {
      console.error('Error checking access status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to check access status';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return null;
    }
  }, [isSignedIn, isLoaded, supabase]);

  // Check access on mount and when auth state changes
  useEffect(() => {
    // Only check once per mount when conditions are met
    if (isLoaded && isSignedIn && !hasCheckedRef.current) {
      hasCheckedRef.current = true;
      // Fire-and-forget: checkAccess handles its own errors internally.
      void checkAccess();
    } else if (isLoaded && !isSignedIn) {
      hasCheckedRef.current = false;
      setState({
        isLoading: false,
        accessStatus: null,
        error: null,
      });
    }
  }, [isLoaded, isSignedIn, checkAccess]);

  // Derive tier and entitlements from access status
  const tier = state.accessStatus?.tier ?? null;
  const entitlements = state.accessStatus?.entitlements ?? DEFAULT_ENTITLEMENTS;

  return {
    ...state,
    checkAccess,
    tier,
    entitlements,
  };
}
